import type { FilterObject } from '@moment/trading-types/filter-expression'
import type { RootState } from '@/app/store'
import type {
  AccountSort,
  AccountSortField,
  SortDirection,
} from '@/graphql/generated/schema'
import type { PageConfigRequest } from '@/types/api/page-config'
import type { AccountsTableColumnId } from '@/types/enums/accounts'

import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit'
import { isEqual } from 'lodash'

import { updatePageConfig } from '@/legacy-api/page-config-slice'
import { defaultPageConfigs } from '@/reducers/defaults/page-configs'
import { FilterInteractionMode } from '@/types/enums/filter-interaction-mode'
import { Page } from '@/types/schema/page-configs'
import {
  ACCOUNT_OVERVIEW_SCHEMA_VERSION,
  type AccountListConfig,
  type ColumnState,
} from '@/types/schema/page-configs/account-list'
import { decodeAccountListFilter } from '@/utils/decode-account-list-filter'

interface AccountListState {
  config: AccountListConfig
  configVersion: string
  defaultConfig: AccountListConfig
  searchTerm: string | null
  filters: FilterObject | null
  sortBy: AccountSort[] | null
  quickFilter: FilterObject | null
  quickFilterName: string | null
  quickFilterId: string | null
  filterName: string | null
  interactionMode: FilterInteractionMode
}

const initialState: AccountListState = {
  config: defaultPageConfigs[Page.AccountList],
  configVersion: ACCOUNT_OVERVIEW_SCHEMA_VERSION,
  defaultConfig: defaultPageConfigs[Page.AccountList],
  searchTerm: null,
  filters: null,
  sortBy: null,
  quickFilter: null,
  quickFilterName: null,
  quickFilterId: null,
  filterName: null,
  interactionMode: FilterInteractionMode.EXPLORING,
}

const accountListSlice = createSlice({
  name: 'accountList',
  reducerPath: 'accountList',
  initialState,
  reducers: {
    initializeAccountListConfig: handleInitializeAccountListConfig,
    reset: handleReset,
    resetAccountListConfig: handleResetAccountListConfig,
    setAccountListConfig: handleSetAccountListConfig,
    updateColumnOrder: handleUpdateColumnOrder,
    updateColumnStates: handleUpdateColumnStates,
    setSearchTerm: handleSetSearchTerm,
    setFilters: handleSetFilters,
    setSortBy: handleSetSortBy,
    setQuickFilter: handleSetQuickFilter,
    setQuickFilterName: handleSetQuickFilterName,
    setQuickFilterId: handleSetQuickFilterId,
    setFilterName: handleSetFilterName,
  },
})

function handleReset(state: AccountListState) {
  Object.assign(state, initialState)
}

function handleInitializeAccountListConfig(
  state: AccountListState,
  action: PayloadAction<{ config: AccountListConfig; version: string }>,
) {
  const defaultColumnState =
    defaultPageConfigs[Page.AccountList].columnState || []
  const userColumnState = action.payload.config.columnState || []

  // Create a set of valid column IDs from the default config
  const defaultColumnIds = new Set(defaultColumnState.map((col) => col.colId))

  // Create a map of user's column preferences for quick lookup
  const userColumnMap = new Map(
    userColumnState
      .filter((col) => defaultColumnIds.has(col.colId))
      .map((col) => [col.colId, col]),
  )

  // Merge columns in the order defined by defaultColumnState
  // For each default column, use user's preferences if they exist, otherwise use default
  const mergedColumnState = defaultColumnState.map((defaultCol) => {
    const userCol = userColumnMap.get(defaultCol.colId)
    return userCol || defaultCol
  })

  // Only update if there were changes (new columns added or invalid columns removed)
  const shouldUpdate =
    userColumnMap.size !== userColumnState.length ||
    mergedColumnState.length !== userColumnState.length

  const config = {
    ...action.payload.config,
    columnState: shouldUpdate ? mergedColumnState : userColumnState,
  }

  state.config = config
  state.configVersion = action.payload.version
  state.defaultConfig = config
}

function handleResetAccountListConfig(state: AccountListState) {
  state.config = state.defaultConfig
}

function handleSetAccountListConfig(
  state: AccountListState,
  action: PayloadAction<Partial<AccountListConfig>>,
) {
  const updatedConfig = {
    ...state.config,
    ...action.payload,
  }
  state.config = updatedConfig
}

function handleUpdateColumnOrder(
  state: AccountListState,
  action: PayloadAction<{
    colId: AccountsTableColumnId
    toIndex: number
  }>,
) {
  const columnState = state.config.columnState || []
  const fromIndex = columnState.findIndex(
    (column) => column.colId === action.payload.colId,
  )

  if (fromIndex === -1) {
    return
  }

  // Reorder the array - array order IS the position
  const updatedColumnState = [...columnState]
  const [movedColumn] = updatedColumnState.splice(fromIndex, 1)
  // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
  updatedColumnState.splice(action.payload.toIndex, 0, movedColumn!)

  state.config.columnState = updatedColumnState
}

function handleUpdateColumnStates(
  state: AccountListState,
  action: PayloadAction<ColumnState[]>,
) {
  // Array order IS the position, no need to set position field
  state.config = { ...state.config, columnState: action.payload }
}

function handleSetSearchTerm(
  state: AccountListState,
  action: PayloadAction<string>,
) {
  state.searchTerm = action.payload
}

function handleSetFilters(
  state: AccountListState,
  action: PayloadAction<string>,
) {
  const nextFilters = decodeAccountListFilter<FilterObject>(action.payload)
  // If the filter is the same, don't update the state
  if (isEqual(state.filters, nextFilters)) {
    return
  }
  state.filters = nextFilters
}

function handleSetSortBy(
  state: AccountListState,
  action: PayloadAction<string>,
) {
  if (!action.payload || action.payload === '') {
    state.sortBy = null
    return
  }
  state.sortBy = action.payload
    .split(',')
    .map((s) => s.split('.'))
    .map(([field, direction]) => {
      return {
        field: field as AccountSortField,
        direction: direction as SortDirection,
      }
    })
}

function handleSetQuickFilter(
  state: AccountListState,
  action: PayloadAction<string>,
) {
  const nextQuickFilter = decodeAccountListFilter<FilterObject>(action.payload)
  if (isEqual(state.quickFilter, nextQuickFilter)) {
    return
  }
  if (!nextQuickFilter) {
    state.quickFilter = null
    state.interactionMode = FilterInteractionMode.EXPLORING
    return
  }
  state.quickFilter = nextQuickFilter
  state.interactionMode = FilterInteractionMode.VIEWING
}

function handleSetQuickFilterName(
  state: AccountListState,
  action: PayloadAction<string>,
) {
  state.quickFilterName = action.payload
}

function handleSetQuickFilterId(
  state: AccountListState,
  action: PayloadAction<string>,
) {
  state.quickFilterId = action.payload
}

function handleSetFilterName(
  state: AccountListState,
  action: PayloadAction<string>,
) {
  state.filterName = action.payload
}

export const persistAccountListConfigAsyncThunk = createAsyncThunk(
  // biome-ignore lint/style/useTemplate: Bulk suppression as part of biome migration
  accountListSlice.reducerPath + '/persistAccountListConfig',
  async (_data, { dispatch, getState, rejectWithValue }) => {
    const state = (getState() as RootState).accountList

    try {
      const request: PageConfigRequest = {
        id: Page.AccountList,
        config: state.config,
        version: state.configVersion,
      }
      await dispatch(updatePageConfig.initiate(request))
    } catch (err) {
      const errorMessage =
        // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
        (err as { response?: { data?: any } }).response?.data ||
        'An unknown error occurred'
      return rejectWithValue(errorMessage)
    }
  },
)

export const {
  initializeAccountListConfig,
  resetAccountListConfig,
  setAccountListConfig,
  updateColumnStates,
} = accountListSlice.actions

// Dispatched only within this module (reducer thunks below and the
// accountListPageQueryParamActions map) — kept unexported so knip doesn't
// flag them as unused exports.
const {
  setSearchTerm,
  setFilters,
  setSortBy,
  setQuickFilter,
  setQuickFilterName,
  setQuickFilterId,
  setFilterName,
} = accountListSlice.actions

// Query param sync actions
export const accountListPageQueryParamActions = {
  searchTerm: setSearchTerm,
  filters: setFilters,
  sortBy: setSortBy,
  quickFilter: setQuickFilter,
  quickFilterName: setQuickFilterName,
  quickFilterId: setQuickFilterId,
  filterName: setFilterName,
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default accountListSlice
